import { ResizeMode, Video } from 'expo-av';
import { router } from 'expo-router';
import React from 'react';
import type { ViewStyle } from 'react-native';
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Colors } from '../constants/Colors';
import { useDynamicStyles } from '../hooks';
import type { WithResponsive } from '../utils/responsivity';
import { TextBold } from './StyledText';

// Define profile interface with only the fields needed by this component
type Profile = {
  first_name?: string;
  last_name?: string;
  photos?: {
    thumbnail_small?: string;
    thumbnail_large?: string;
    small?: string;
    medium?: string;
    large?: string;
    original?: string;
  };
  videos?: {
    thumbnail_small?: string;
    thumbnail_large?: string;
    small?: string;
    medium?: string;
    large?: string;
    original?: string;
  };
};

// Define profile photo interface based on API model
type ProfilePhoto = {
  user_id: string;
  thumbnail_small: string;
  thumbnail_large: string;
  small: string;
  medium: string;
  large: string;
  original: string;
  is_active: boolean;
};

type Props = {
  userId?: string;
  imgSize: keyof Omit<ProfilePhoto, 'user_id' | 'is_active'>;
  styleSize: number;
  tempUrl?: string;
  forcePhoto?: boolean;
  shouldNavigateOnClick?: boolean;
  style?: ViewStyle;
  profile?: Profile;
  isLoading?: boolean;
};

export function ProfileImg({
  userId,
  imgSize = 'thumbnail_small',
  styleSize,
  tempUrl,
  forcePhoto = true,
  style,
  profile,
  isLoading,
}: Props) {
  const videoUrl = profile?.videos?.[imgSize];
  const imgUrl = tempUrl ?? profile?.photos?.[imgSize];
  const isDisplayingVideo = videoUrl && !forcePhoto;
  const styles = useDynamicStyles(createStyles, {
    styleSize,
    hasOnClick: Boolean(isDisplayingVideo),
  });

  const handleOnPress = () => {
    if (isDisplayingVideo && userId) {
      router.navigate(
        `/watch?userId=${userId}&video=${encodeURIComponent(videoUrl ?? '')}`,
      );
    }
  };

  // Get initials from profile name
  const getInitials = () => {
    const firstInitial = profile?.first_name?.[0]?.toUpperCase() ?? '';
    const lastInitial = profile?.last_name?.[0]?.toUpperCase() ?? '';
    return `${firstInitial}${lastInitial}`;
  };

  if (!userId || isLoading || !imgUrl) {
    const initials = getInitials();
    return (
      <View style={[styles.photoContainer, styles.placeholderContainer, style]}>
        {initials ? (
          <TextBold style={styles.initials}>{initials}</TextBold>
        ) : null}
      </View>
    );
  }

  return (
    <View style={[styles.photoContainer, style]}>
      <TouchableOpacity onPress={handleOnPress}>
        {isDisplayingVideo ? (
          <Video
            source={{ uri: videoUrl }}
            rate={1.0}
            volume={1.0}
            resizeMode={ResizeMode.COVER}
            isMuted
            shouldPlay
            isLooping
            style={styles.videoContainer}
            videoStyle={styles.video}
          />
        ) : (
          <Image
            source={{ uri: imgUrl }}
            aria-label={`${profile?.first_name} ${profile?.last_name} Profile Photo`}
            style={styles.photo}
          />
        )}
      </TouchableOpacity>
    </View>
  );
}

const createStyles = ({
  styleSize,
  hasOnClick,
}: WithResponsive<Props & { hasOnClick: boolean }>) =>
  StyleSheet.create({
    photoContainer: {
      backgroundColor: Colors.opacity05,
      width: styleSize,
      height: styleSize,
      borderRadius: styleSize,
      pointerEvents: hasOnClick ? undefined : 'none',
    },
    placeholderContainer: {
      backgroundColor: Colors.purple1opacity50,
      justifyContent: 'center',
      alignItems: 'center',
    },
    initials: {
      color: Colors.text,
      fontSize: styleSize ? styleSize * 0.4 : 20,
    },
    photo: {
      width: styleSize,
      height: styleSize,
      borderRadius: styleSize,
    },
    videoContainer: {
      width: styleSize,
      height: styleSize,
      display: 'flex',
      alignItems: 'center',
    },
    video: {
      position: 'relative',
    },
  });
